import { z } from "zod";
import { currencyCodes as currencies } from "./constants";

export const IDSchema = z.number().or(z.string());
export type ID = z.infer<typeof IDSchema>;

export const CurrencySchema = z.enum(currencies);
export type Currency = z.infer<typeof CurrencySchema>;

export const FundSizeSchema = z.record(CurrencySchema, z.string());
export type FundSize = z.infer<typeof FundSizeSchema>;

export const NumberOfHoldingsSchema = z.object({
	NumberOfPositions: z.string().optional(),
	Fund: z.string().optional(),
});
export type NumberOfHoldings = z.infer<typeof NumberOfHoldingsSchema>;

export const DocumentTupleSchema = z.string().array();
export type DocumentTuple = z.infer<typeof DocumentTupleSchema>;

export const RawValueSchema = z.union([
	z.record(z.string()),
	z.string(),
	z.string().array(),
	DocumentTupleSchema.array(),
	z.null(),
	z.boolean(),
	z.number(),
	z.date(),
]);
export type RawValue = z.infer<typeof RawValueSchema>;

export const APIShareClassSchema = z.object({
	data: RawValueSchema.array(),
	id: z.number(),
	currency: CurrencySchema.nullable(),
});
export type APIShareClass = z.infer<typeof APIShareClassSchema>;

export const FormatObjSchema = z.custom(
	(val) => {
		const isObj = typeof val === "object";
		const hasValidValues = Object.entries(val).every(
			([key, val]) =>
				currencies.includes(key as Currency) && typeof val === "string",
		);
		return isObj && hasValidValues;
	},
	{
		message: "Invalid format object",
	},
);
export type FormatObj = z.infer<typeof FormatObjSchema>;

export const FormatRuleSchema = z.object({
	min_value: z.string().or(z.number()),
	formats: FormatObjSchema,
});
export type FieldFormatRule = z.infer<typeof FormatRuleSchema>;

export const FieldTypeSchema = z.union([
	z.literal("date"),
	z.literal("dict"),
	z.literal("image"),
	z.literal("string"),
	z.literal("currency"),
	z.literal("number"),
	z.literal("document"),
	z.literal("boolean"),
]);
export type FieldType = z.infer<typeof FieldTypeSchema>;

export const APIFundSchema = z.object({
	data: RawValueSchema.array(),
	share_classes: APIShareClassSchema.array(),
	profile_ids: IDSchema.array().optional(),
	id: IDSchema,
	primary_shareclass: z.null().optional(),
	currency: CurrencySchema,
	fund_badge_info: z.number().nullable(),
});
export type APIFund = z.infer<typeof APIFundSchema>;

export const APIProfileSchema = z.object({
	id: IDSchema,
	data: RawValueSchema.array(),
});
export type APIProfile = z.infer<typeof APIProfileSchema>;

export const TypedAPIFundSchema = z.object({
	type: z.literal("fund"),
	fund: APIFundSchema,
});
export type TypedAPIFund = z.infer<typeof TypedAPIFundSchema>;

export const TypedAPIShareClassSchema = z.object({
	type: z.literal("shareclass"),
	shareclass: APIShareClassSchema,
});
export type TypedAPIShareClass = z.infer<typeof TypedAPIShareClassSchema>;

export const TypedAPIProfileSchema = z.object({
	type: z.literal("profile"),
	profile: APIProfileSchema,
});
export type TypedAPIProfile = z.infer<typeof TypedAPIProfileSchema>;

export const TypedAPIObjSchema = z.discriminatedUnion("type", [
	TypedAPIFundSchema,
	TypedAPIShareClassSchema,
	TypedAPIProfileSchema,
]);
export type TypedAPIObj = z.infer<typeof TypedAPIObjSchema>;

export const MetaFieldSchema = z.object({
	code_name: z.string(),
	label: z.string(),
	format: z.string().nullable(),
	null_replacement: z.string(),
	type: FieldTypeSchema,
	currency: CurrencySchema.nullable().optional(),
	format_rules: FormatRuleSchema.array().optional(),
});
export type MetaField = z.infer<typeof MetaFieldSchema>;

export const MetaDataSchema = z.object({
	fund_fields: MetaFieldSchema.array(),
	share_class_fields: MetaFieldSchema.array(),
	listing_fields: z.unknown().array(),
	as_at_date: z.coerce.date(),
	performance_end_date: z.coerce.date(),
	quarter_end_date: z.coerce.date(),
	year_end_date: z.coerce.date(),
	audience_name: z.string(),
	profile_fields: MetaFieldSchema.array().optional(),
});
export type MetaData = z.infer<typeof MetaDataSchema>;

export const MetaDataFieldPropSchema = MetaDataSchema.pick({
	fund_fields: true,
	share_class_fields: true,
	listing_fields: true,
	profile_fields: true,
}).keyof();
export type MetaDataFieldProp = z.infer<typeof MetaDataFieldPropSchema>;

// export const ProfileSchema = z.object({
// 	id: IDSchema,
// 	data: z.string().array(),
// });
// export type Profile = z.infer<typeof ProfileSchema>;

export const FundExplorerAPIResponseSchema = z.object({
	metadata: MetaDataSchema,
	funds: APIFundSchema.array(),
	profiles: APIProfileSchema.array().optional(),
});
export type FundExplorerAPIResponse = z.infer<
	typeof FundExplorerAPIResponseSchema
>;
